import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  PlacementWithLogical,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Select,
  SkeletonText,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { TAutoCompleteOption } from "./AutoComplete.types";
import { CheckIcon, SearchIcon } from "@chakra-ui/icons";
import { useDebounce } from "utils";
import { DEFAULT_PAGE } from "common";

interface Props {
  placement?: PlacementWithLogical;
  placeholder?: string;
  multiple?: boolean;
  value?: TAutoCompleteOption[];
  options?: TAutoCompleteOption[];
  loading?: boolean;
  pageNumber?: number;
  pageSize?: number;
  total?: number;
  maxW?: string | number;
  w?: string | number;
  onLoadMore?: () => void;
  onChange?: (data: TAutoCompleteOption[]) => void;
  onSearch?: (keyword?: string) => void;
}

const AutoComplete = ({
  placement = "bottom-start",
  placeholder,
  multiple,
  value = [],
  options = [],
  loading,
  pageNumber = 0,
  pageSize = 0,
  total = 0,
  maxW,
  w,
  onLoadMore,
  onChange,
  onSearch,
}: Props) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [searchValue, setSearchValue] = useState<string>();
  const searchValueDebounce = useDebounce(searchValue, 300);

  const handleSelectOption = (
    option: TAutoCompleteOption,
    isChecked: boolean
  ) => {
    if (multiple) {
      if (isChecked) {
        onChange?.(value.filter((i) => i.value !== option.value));
      } else {
        onChange?.([...value, option]);
      }
    } else {
      onChange?.(isChecked ? [] : [option]);
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>): void => {
    const element = e.currentTarget;
    const isScrollEndOfElement =
      Math.round(element.scrollTop + element.clientHeight) >= element.scrollHeight - 5;

    if (!pageSize || !total) return;

    const maxPage = Math.ceil(total / pageSize);
    const isLastPage = pageNumber >= maxPage;

    if (loading || isLastPage) return;
    if (isScrollEndOfElement) {
      onLoadMore?.();
    }
  };


  useEffect(() => {
    if (typeof searchValueDebounce === "string") {
      onSearch?.(searchValueDebounce);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValueDebounce]);

  return (
    <Popover isOpen={isOpen} onClose={onClose} placement={placement}>
      <PopoverTrigger>
        <Box className="AutoComplete" cursor="pointer" onClick={onOpen}>
          <Box pointerEvents="none">
            <Select
              placeholder={
                value?.map((option) => option.label)?.join(", ") || placeholder
              }
              maxW={maxW}
              w={w}
            />
          </Box>
        </Box>
      </PopoverTrigger>
      <PopoverContent>
        {onSearch && (
          <PopoverHeader>
            <InputGroup>
              <InputLeftElement
                pointerEvents="none"
                children={<SearchIcon color="#353333" />}
              />
              <Input
                placeholder="Search"
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.target.value);
                }}
              />
            </InputGroup>
          </PopoverHeader>
        )}

        <PopoverBody>
          {loading && pageNumber === DEFAULT_PAGE ? (
            <SkeletonText noOfLines={10} />
          ) : (
            <>
              {options.length === 0 ? (
                <Text p="20px" textAlign="center" fontWeight="bold">
                  No records found
                </Text>
              ) : (
                <>
                  <Box
                    maxHeight="400px"
                    overflow="scroll"
                    onScroll={handleScroll}
                    data-testid="autocomplete-options-wrapper"
                  >
                    {options.map((option) => {
                      const isActive = value
                        ?.map((i) => i.value)
                        .includes(option.value);

                      return (
                        <Flex
                          key={option.value}
                          align="center"
                          justify="space-between"
                          p="12px"
                          cursor="pointer"
                          borderRadius="4px"
                          backgroundColor={isActive ? "blue.50" : undefined}
                          _hover={{
                            backgroundColor: "blue.50",
                          }}
                          _notLast={{
                            mb: "4px",
                          }}
                          onClick={() => handleSelectOption(option, !!isActive)}
                          data-testid="autocomplete-option"
                        >
                          <Text
                            noOfLines={1}
                            color={isActive ? "blue.500" : undefined}
                          >
                            {option.label}
                          </Text>
                          {isActive && <CheckIcon color="blue.500" />}
                        </Flex>
                      );
                    })}
                  </Box>
                  {loading && <SkeletonText p="12px" noOfLines={2} />}
                </>
              )}
            </>
          )}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default AutoComplete;
